import SeoFooter from "../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Shield, Clock, Car, Users, Award, Target, Heart, Phone, Mail, Star, CheckCircle2, ArrowRight
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const AboutPage = () => {
  const whyChooseUs = [
    {
      icon: Users,
      title: "Professional Chauffeurs",
      description: "Our drivers are more than just operators; they are highly trained professionals committed to your safety and privacy."
    },
    {
      icon: Car,
      title: "A Fleet for Every Occasion",
      description: "From sleek executive sedans to spacious, high-end SUVs, our fleet is meticulously maintained to the highest standards."
    },
    {
      icon: Clock,
      title: "Reliability Guaranteed",
      description: "We track flights and traffic in real-time to ensure we are always there before you need us."
    }
  ];

  const values = [
    { icon: Shield, title: "Safety First", desc: "Your security is our top priority on every journey" },
    { icon: Star, title: "Excellence", desc: "We strive for perfection in every detail" },
    { icon: Heart, title: "Customer Focus", desc: "Your satisfaction drives everything we do" },
    { icon: Award, title: "Professionalism", desc: "Trained, vetted, and experienced chauffeurs" }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Limo Near Me",
      "description": "Premium chauffeur and limousine services across Australia. Redefining luxury travel with professional chauffeurs, premium fleet, and guaranteed reliability.",
      "foundingDate": "2020",
      "areaServed": "Australia",
      "telephone": "+61481437772",
      "email": "info@limonearme.com.au",
      "url": "https://www.limonearme.com.au"
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>About Us | Limo Near Me | Premium Chauffeur Services Australia</title>
          <meta name="description" content="Learn about Limo Near Me - Australia's premier luxury chauffeur service. Professional drivers, premium fleet, and guaranteed reliability. Redefining luxury travel across Brisbane, Sydney, Melbourne, Gold Coast." />
          <meta name="keywords" content="about limo near me, luxury chauffeur company, limousine service Australia, professional drivers, premium transport" />
          <link rel="canonical" href="https://www.limonearme.com.au/about" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/about" className="text-sm text-foreground font-medium">About Us</Link>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Redefining Luxury Travel Across Australia
                </h1>
                <p className="text-xl text-gray-300 mb-8">
                  At LimoNearMe, we believe that transportation is more than just getting from point A to point B—it's about the experience, the comfort, and the peace of mind that comes with a professional service.
                </p>
                <Link to="/book">
                  <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                    Experience the Difference
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden md:block"
              >
                <img 
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop" 
                  alt="Professional chauffeur service" 
                  className="rounded-2xl shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-foreground mb-6">Our Story</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Built on years of industry experience and a passion for premium service, LimoNearMe was founded to bridge the gap between standard transport and true luxury. We understand that our clients—whether corporate executives, wedding parties, or travelers—value punctuality and discretion above all else.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Why Choose Us?</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We go above and beyond to ensure every journey exceeds your expectations.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Mission Section */}
        <section className="py-20 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Target className="h-16 w-16 mx-auto mb-6 text-white/80" />
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                To provide Australia's most reliable and elegant limousine service, ensuring every passenger feels like a VIP from the moment the door is opened.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Our Values</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6"
                >
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { number: "10,000+", label: "Happy Customers" },
                { number: "6", label: "Cities Served" },
                { number: "24/7", label: "Availability" },
                { number: "4.9★", label: "Average Rating" }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="text-4xl font-bold text-primary mb-2">{stat.number}</p>
                  <p className="text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience the Difference?</h2>
            <p className="text-primary-foreground/80 mb-8">
              Book your luxury transfer today and discover why thousands trust LimoNearMe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" variant="secondary" className="rounded-full px-8">
                  Book Your Ride Now
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default AboutPage;
